import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { knowledgeBaseAPI } from '../../../api/services';
import { KBBlock, KBBlockType, KnowledgeBaseDoc } from '../../../types';
import BlockRenderer from '../../KnowledgeBase/blocks/BlockRenderer';
import BlockMenu from '../../KnowledgeBase/blocks/BlockMenu';

function makeBlock(type: KBBlockType): KBBlock {
  const base: KBBlock = { id: uuidv4(), type, content: '' };
  if (type === 'bullet_list' || type === 'ordered_list') return { ...base, data: { items: [''] } };
  if (type === 'checklist') return { ...base, data: { items: [{ text: '', checked: false }] } };
  if (type === 'table') return { ...base, data: { headers: ['Column 1', 'Column 2'], rows: [['', '']] } };
  if (type === 'toggle') return { ...base, data: { body: '', isOpen: false } };
  if (type === 'callout') return { ...base, data: { variant: 'info' } };
  if (type === 'code') return { ...base, data: { language: 'typescript' } };
  if (type === 'image') return { ...base, data: { url: '', caption: '', alt: '' } };
  if (type === 'video') return { ...base, data: { url: '', caption: '' } };
  if (type === 'file') return { ...base, data: { fileUrl: '', fileName: '', fileSize: 0 } };
  if (type === 'spacer') return { ...base, data: { size: 2 } };
  if (type === 'progress') return { ...base, data: { value: 50, label: '', color: 'blue' } };
  if (type === 'status_badge') return { ...base, data: { status: 'not_started' } };
  if (type === 'bookmark') return { ...base, data: { url: '', title: '', description: '' } };
  return base;
}

interface Props {
  docId: string;
  onClose: () => void;
}

const KBEditorModal: React.FC<Props> = ({ docId, onClose }) => {
  const [doc, setDoc] = useState<Partial<KnowledgeBaseDoc> | null>(null);
  const [blocks, setBlocks] = useState<KBBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [blockMenuAnchor, setBlockMenuAnchor] = useState<{ top: number; left: number } | null>(null);
  const [insertAfterIndex, setInsertAfterIndex] = useState<number>(-1);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved' | 'saving'>('saved');
  const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    knowledgeBaseAPI.get(docId)
      .then((data) => { setDoc(data); setBlocks(data.blocks ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [docId]);

  const triggerAutoSave = useCallback(() => {
    setSaveStatus('unsaved');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saving');
      setDoc(currentDoc => {
        setBlocks(currentBlocks => {
          const payload = { ...currentDoc, blocks: currentBlocks };
          knowledgeBaseAPI.update(docId, payload)
            .then(() => setSaveStatus('saved'))
            .catch(() => setSaveStatus('unsaved'));
          return currentBlocks;
        });
        return currentDoc;
      });
    }, 1500);
  }, [docId]);

  const updateDocField = (updates: Partial<KnowledgeBaseDoc>) => {
    setDoc(prev => prev ? { ...prev, ...updates } : prev);
    triggerAutoSave();
  };

  const updateBlock = useCallback((index: number, updates: Partial<KBBlock>) => {
    setBlocks(prev => { const next = [...prev]; next[index] = { ...next[index], ...updates }; return next; });
    triggerAutoSave();
  }, [triggerAutoSave]);

  const addBlock = useCallback((type: KBBlockType, afterIndex: number) => {
    const block = makeBlock(type);
    setBlocks(prev => { const next = [...prev]; next.splice(afterIndex + 1, 0, block); return next; });
    setEditingBlockId(block.id);
    triggerAutoSave();
  }, [triggerAutoSave]);

  const removeBlock = useCallback((index: number) => {
    setBlocks(prev => prev.filter((_, i) => i !== index));
    setEditingBlockId(null);
    triggerAutoSave();
  }, [triggerAutoSave]);

  const moveBlock = useCallback((from: number, to: number) => {
    if (from === to) return;
    setBlocks(prev => { const next = [...prev]; const [item] = next.splice(from, 1); next.splice(to, 0, item); return next; });
    setSaveStatus('unsaved');
  }, []);

  const openBlockMenu = (e: React.MouseEvent, afterIndex: number) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setBlockMenuAnchor({ top: rect.bottom + 4, left: rect.left });
    setInsertAfterIndex(afterIndex);
    setShowBlockMenu(true);
  };

  // Save before close — use state setters to read current values
  const handleClose = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Always save on close to capture any pending changes
    setDoc(currentDoc => {
      setBlocks(currentBlocks => {
        if (currentDoc) {
          knowledgeBaseAPI.update(docId, { ...currentDoc, blocks: currentBlocks }).catch(() => {});
        }
        return currentBlocks;
      });
      return currentDoc;
    });
    onClose();
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  if (!doc) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <p className="text-sm text-gray-500">Document not found</p>
        <button onClick={onClose} className="text-xs text-blue-600 mt-2">Close</button>
      </div>
    </div>
  );

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3 text-xs flex-1 min-w-0">
          <button className="text-2xl hover:bg-gray-100 rounded p-0.5 transition-colors" title="Change icon"
            onClick={() => {
              const icons = ['📄', '📝', '📚', '📖', '🗒️', '📋', '📌', '🔍', '💡', '🚀', '⚡', '🎯', '🛠️', '📊'];
              const cur = icons.indexOf(doc.icon ?? '📄');
              updateDocField({ icon: icons[(cur + 1) % icons.length] });
            }}>
            {doc.icon || '📄'}
          </button>
          <input value={doc.title ?? ''} onChange={e => updateDocField({ title: e.target.value })}
            placeholder="Untitled document" className="text-sm font-semibold text-gray-900 border-none focus:outline-none bg-transparent flex-1 min-w-0" />
          <span className={`text-[10px] shrink-0 ${saveStatus === 'saved' ? 'text-green-500' : saveStatus === 'saving' ? 'text-yellow-500' : 'text-gray-400'}`}>
            {saveStatus === 'saved' ? '✓ Saved' : saveStatus === 'saving' ? 'Saving…' : 'Unsaved'}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {/* Visibility selector */}
          <select value={doc.visibility || 'private'} onChange={e => updateDocField({ visibility: e.target.value as any })}
            className="text-[10px] border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-blue-400 cursor-pointer">
            <option value="private">🔒 Private</option>
            <option value="department">🏢 Department</option>
            <option value="team">👥 Team</option>
            <option value="public">🌐 Public</option>
          </select>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {/* Editor body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-6">
          {/* Description */}
          <input value={doc.description ?? ''} onChange={e => updateDocField({ description: e.target.value })}
            placeholder="Add a description…" className="w-full text-sm text-gray-500 border-none outline-none placeholder-gray-300 bg-transparent mb-6" />

          {/* Blocks */}
          <div className="space-y-1">
            {blocks.map((block, index) => {
              const isHovered = hoveredBlockIndex === index;
              const isDragTarget = dragOverIndex === index && dragFromIndex !== null && dragFromIndex !== index;
              return (
                <div key={block.id}
                  className={`flex items-start gap-1 rounded-lg px-1 py-0.5 transition-colors ${isDragTarget ? 'bg-blue-50 ring-2 ring-blue-300' : isHovered ? 'bg-gray-50' : ''}`}
                  onMouseEnter={() => setHoveredBlockIndex(index)} onMouseLeave={() => setHoveredBlockIndex(null)}
                  onDragOver={e => { e.preventDefault(); setDragOverIndex(index); }}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverIndex(null); }}
                  onDrop={e => { e.preventDefault(); if (dragFromIndex !== null && dragFromIndex !== index) moveBlock(dragFromIndex, index); setDragFromIndex(null); setDragOverIndex(null); }}>

                  {/* Left toolbar */}
                  <div className="flex-shrink-0 flex flex-col items-center gap-0.5 pt-1 w-6"
                    style={{ opacity: isHovered ? 1 : 0, pointerEvents: isHovered ? 'auto' : 'none' }}>
                    <button draggable onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragFromIndex(index); }}
                      onDragEnd={() => { setDragFromIndex(null); setDragOverIndex(null); }}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded cursor-grab active:cursor-grabbing" title="Drag">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6a2 2 0 100-4 2 2 0 000 4zM8 14a2 2 0 100-4 2 2 0 000 4zM8 22a2 2 0 100-4 2 2 0 000 4zM16 6a2 2 0 100-4 2 2 0 000 4zM16 14a2 2 0 100-4 2 2 0 000 4zM16 22a2 2 0 100-4 2 2 0 000 4z" /></svg>
                    </button>
                    <button onClick={e => { e.stopPropagation(); moveBlock(index, index - 1); }} disabled={index === 0}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-20 rounded" title="Up">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button onClick={e => { e.stopPropagation(); moveBlock(index, index + 1); }} disabled={index === blocks.length - 1}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-20 rounded" title="Down">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    <button onClick={e => openBlockMenu(e, index)}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded font-bold text-xs" title="Insert below">+</button>
                  </div>

                  {/* Block content */}
                  <div className="flex-1 min-w-0" onClick={e => { if ((e.target as HTMLElement).closest('button, input, textarea, select')) return; setEditingBlockId(block.id); }}>
                    <BlockRenderer block={block} isEditing={editingBlockId === block.id} onChange={updates => updateBlock(index, updates)} onFinish={() => setEditingBlockId(null)} allBlocks={blocks} />
                  </div>

                  {/* Delete */}
                  <button onClick={e => { e.stopPropagation(); removeBlock(index); }}
                    className="flex-shrink-0 mt-1 w-6 h-6 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded"
                    style={{ opacity: isHovered ? 1 : 0, pointerEvents: isHovered ? 'auto' : 'none' }} title="Delete">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              );
            })}

            {/* Add block button */}
            <div className="pt-4 pl-8">
              <button onClick={e => openBlockMenu(e, blocks.length - 1)}
                className="flex items-center gap-2 text-gray-300 hover:text-gray-500 transition-colors text-sm">
                <span className="w-6 h-6 flex items-center justify-center border border-dashed border-gray-200 rounded hover:border-gray-400">+</span>
                Add block
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Block menu */}
      {showBlockMenu && blockMenuAnchor && (
        <div className="fixed z-[60]" style={{ top: blockMenuAnchor.top, left: blockMenuAnchor.left }}>
          <BlockMenu onSelect={type => { addBlock(type, insertAfterIndex); setShowBlockMenu(false); }} onClose={() => setShowBlockMenu(false)} />
        </div>
      )}
    </>
  );
};

export default KBEditorModal;
